import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { resolveAssetUrl } from '@/lib/assetUrl';

/**
 * A tenant's logo, falling back to its initial on a branded square (EIP-561).
 *
 * Three surfaces rendered this by hand and all three got it wrong the same two
 * ways: they used the stored URL verbatim (so backend-served logos 404'd on the
 * frontend origin) and they had no onError, so the initials branch was only
 * reachable when the field was ABSENT. A present-but-unloadable URL rendered a
 * broken-image glyph forever.
 *
 * The correct version already existed a hundred lines above one of them, in
 * DashboardLayout's sidebar. Nobody looked up. Hence one component.
 *
 * @param {string} props.name        tenant display name; supplies the initial
 * @param {string} [props.src]       stored icon_url/logo_url, absolute or relative
 * @param {string} [props.color]     brand colour behind the initial
 * @param {string} [props.className] box sizing and shape, e.g. "w-6 h-6 rounded-md"
 * @param {string} [props.textClassName] initial's typography
 * @param {string} [props.imgClassName]  extra classes for the <img> only
 */
export function TenantIcon({
  name,
  src,
  color,
  className,
  textClassName = 'text-xs font-bold',
  imgClassName,
}) {
  const resolved = resolveAssetUrl(src);
  const [failed, setFailed] = useState(false);

  // A row can be recycled onto a different tenant (the switcher list re-renders
  // in place), so a stale failure must not blank the next tenant's logo.
  useEffect(() => {
    setFailed(false);
  }, [resolved]);

  const initial = (name || 'T').charAt(0);

  if (resolved && !failed) {
    return (
      <img
        src={resolved}
        alt={name || 'Tenant'}
        onError={() => setFailed(true)}
        className={cn('object-contain shrink-0', className, imgClassName)}
      />
    );
  }

  return (
    <div
      aria-label={name || 'Tenant'}
      className={cn(
        'shrink-0 flex items-center justify-center text-white',
        textClassName,
        className
      )}
      style={{ backgroundColor: color || '#6366f1' }}
    >
      {initial}
    </div>
  );
}

export default TenantIcon;
